import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from '../AuthContext';
import "./AdminDeletedProductList.css";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminDeletedProductList() {
  const { authFetch } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setIsLoading, isLoading } = useContext(LoadingContext);
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [deletionPeriodFilter, setDeletionPeriodFilter] = useState("");

  const handleBack = () => {
    navigate("/admin/products");
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/products/deleted?productType=selling`);
        if (!response.ok) {
          throw new Error("Failed to fetch deleted selling products");
        }
        const data = await response.json();
        setProducts(data.products || []);
        setFilteredProducts(data.products || []);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        // Silent fail
      }
    };

    fetchProducts();
    fetchCategories();
  }, [navigate, setIsLoading, authFetch]);


  useEffect(() => {
    if (!products.length) return;

    let filtered = [...products];

    if (nameFilter) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (product) => product.categoryID === parseInt(categoryFilter)
      );
    }

    if (deletionPeriodFilter) {
      const days = parseInt(deletionPeriodFilter);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      filtered = filtered.filter((product) => {
        const deletedDate = new Date(product.deletedAt);
        return deletedDate >= cutoffDate;
      });
    }

    setFilteredProducts(filtered);
  }, [products, nameFilter, categoryFilter, deletionPeriodFilter]);

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const handleDeletionPeriodChange = (e) => {
    setDeletionPeriodFilter(e.target.value);
  };

  const clearFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setDeletionPeriodFilter("");
  };

  const handleRestoreProduct = async (productId) => {
    const result = await Swal.fire({
      title: 'Restore Product',
      text: 'Are you sure you want to restore this product?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#45a049',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const productResponse = await fetch(
          `${API_BASE_URL}/products/${productId}`
        );
        if (!productResponse.ok) {
          throw new Error("Failed to fetch product data");
        }
        const productData = await productResponse.json();

        const formData = new FormData();
        formData.append("Name", productData.name);
        formData.append("Description", productData.description);
        if (productData.price !== null && productData.price !== undefined) {
          formData.append("Price", productData.price);
        }
        formData.append("CategoryID", productData.categoryID);
        if (productData.batchStockQuantity !== null && productData.batchStockQuantity !== undefined) {
          formData.append("BatchStockQuantity", productData.batchStockQuantity);
        }
        if (productData.stockQuantity !== null && productData.stockQuantity !== undefined) {
          formData.append("StockQuantity", productData.stockQuantity);
        }
        formData.append("MOQ", productData.moq);
        formData.append("ProductCode", productData.productCode);
        formData.append("IsRedeemable", false);
        formData.append("deletedAt", "");
        formData.append("updateSpecifications", "false");
        formData.append("updateVariants", "false");

        const response = await authFetch(
          `${API_BASE_URL}/products/${productId}`,
          {
            method: "PUT",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to restore product");
        }

        setProducts((prevProducts) => {
          return prevProducts.filter(
            (product) => product.productID !== productId
          );
        });

        Swal.fire({
          title: 'Restored!',
          text: 'The product has been successfully restored.',
          icon: 'success',
          timer: 1500,
          confirmButtonColor: '#45a049'
        });
      } catch (error) {
        setError(error);
        Swal.fire({
          title: 'Error!',
          text: `Failed to restore product: ${error.message}`,
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="admin-deleted-product-list-container">
        <div className="admin-deleted-product-list-header">
          <button
            className="admin-deleted-product-list-back-btn"
            onClick={handleBack}
          >
            <i className="fas fa-arrow-left"></i> Back to Products
          </button>
        </div>
        <div className="admin-deleted-product-list-error">
          <i className="fas fa-exclamation-triangle"></i> Error: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-deleted-product-list-container">
      {/* Back to Product Section */}
      <div className="admin-deleted-product-list-header">
        <button
          className="admin-deleted-product-list-back-btn"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left"></i> Back to Products
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-deleted-product-list-title">
        <h1>Removed Selling Products</h1>
      </div>

      {/* Filter Section */}
      <div className="admin-deleted-product-list-filter">
        <div className="admin-deleted-product-list-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Filter by product name"
            value={nameFilter}
            onChange={handleNameFilterChange}
            className="admin-deleted-product-list-filter-input"
          />
        </div>

        <div className="admin-deleted-product-list-filter-group">
          <label>Category</label>
          <select
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            className="admin-deleted-product-list-filter-select"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.categoryID} value={category.categoryID}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-deleted-product-list-filter-group">
          <label>Deleted Within</label>
          <select
            value={deletionPeriodFilter}
            onChange={handleDeletionPeriodChange}
            className="admin-deleted-product-list-filter-select"
          >
            <option value="">Any Time</option>
            <option value="15">Last 15 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="60">Last 60 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="120">Last 120 Days</option>
          </select>
        </div>

        <button
          className="admin-deleted-product-list-filter-btn"
          onClick={clearFilters}
        >
          Search
        </button>
      </div>

      <div className="admin-deleted-product-list-grid">
        {filteredProducts.length === 0 ? (
          <div className="admin-deleted-product-list-empty">
            <div className="admin-deleted-product-list-empty-icon">
              <i className="fas fa-trash-alt"></i>
            </div>
            <p className="admin-deleted-product-list-empty-text">
              No deleted selling products found matching your criteria.
            </p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product.productID}
              className="admin-deleted-product-list-card"
            >
              <div className="admin-deleted-product-list-image-container">
                <img
                  className="admin-deleted-product-list-image"
                  src={`${API_BASE_URL}/products/image/${product.productID}`}
                  alt={product.name}
                />
              </div>
              <div className="admin-deleted-product-list-info">
                <h2 className="admin-deleted-product-list-name">
                  {product.name}
                </h2>
                <div className="admin-deleted-product-list-details">
                  <div className="admin-deleted-product-list-detail">
                    <span className="admin-deleted-product-list-detail-label">
                      Product Code:
                    </span>
                    <span className="admin-deleted-product-list-detail-value">
                      {product.productCode}
                    </span>
                  </div>
                  <div className="admin-deleted-product-list-detail">
                    <span className="admin-deleted-product-list-detail-label">
                      Category:
                    </span>
                    <span className="admin-deleted-product-list-detail-value">
                      {categories.find(cat => cat.categoryID === product.categoryID)?.name || 'Unknown'}
                    </span>
                  </div>
                  <div className="admin-deleted-product-list-detail">
                    <span className="admin-deleted-product-list-detail-label">
                      Deleted On:
                    </span>
                    <span className="admin-deleted-product-list-detail-value">
                      {new Date(product.deletedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="admin-deleted-product-list-actions">
                <button
                  className="admin-deleted-product-list-btn-restore"
                  onClick={() => handleRestoreProduct(product.productID)}
                >
                  <i className="fas fa-trash-restore"></i> Restore Product
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminDeletedProductList;